































































































import {
  defineNuxtComponent,
  PropType,
  computed,
  ref,
  watch
} from '~/utils/nuxt3-migration'
import CFormGroup from '~/components/shared/configurable/form/CFormGroup.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import { Facet, Searchable } from '~/models/search/types'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import { useDeps } from '~/compositions/dependency-container'
import SearchService from '~/services/search/SearchService'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_SEARCH_NS,
  SearchState
} from '~/store/modules/shared/classifieds/search/state'
import { formatNumber } from '~/utils/number'
import { useUserAgent } from '~/compositions/user-agent'

export default defineNuxtComponent({
  components: { CCustomSelect, CFormGroup },
  props: {
    leasingOptionsFacet: {
      type: Object as PropType<Facet>,
      required: true
    },
    leasingExtrasFacet: {
      type: Object as PropType<Facet>,
      required: true
    }
  },
  setup(props, { emit }) {
    const [searchService] = useDeps(SearchService)
    const { state: classifiedSearchState } = useNamespacedStore<SearchState>(
      CLASSIFIED_SEARCH_NS
    )
    const { isPc } = useUserAgent()

    const leaseFrom = ref(
      props.leasingOptionsFacet.values.selected['lease-from']
    )
    const leaseTo = ref(props.leasingOptionsFacet.values.selected['lease-to'])
    const downpaymentFrom = ref(
      props.leasingOptionsFacet.values.selected['lease-downpayment-from']
    )
    const downpaymentTo = ref(
      props.leasingOptionsFacet.values.selected['lease-downpayment-to']
    )
    const withVat = ref(props.leasingOptionsFacet.values.selected['with-vat'])
    const duration = ref(props.leasingOptionsFacet.values.selected.duration)
    const mileage = ref(
      props.leasingOptionsFacet.values.selected['yearly-mileage']
    )
    const extras = ref(props.leasingExtrasFacet.values.selected)
    const resultsCount = ref(classifiedSearchState.stats.total)
    const loadingCount = ref(false)
    const hasSomeFieldChanged = ref(false)

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const totalStatsResults = computed(() => classifiedSearchState.stats.total)

    const leaseFromOptions = computed(() =>
      props.leasingOptionsFacet.searchables.lease.map((s: Searchable) => ({
        label: s.n,
        value: s.v,
        disabled:
          leaseTo.value !== null && parseFloat(s.v) > parseFloat(leaseTo.value)
      }))
    )

    const leaseToOptions = computed(() =>
      props.leasingOptionsFacet.searchables.lease.map((s: Searchable) => ({
        label: s.n,
        value: s.v,
        disabled:
          leaseFrom.value !== null &&
          parseFloat(s.v) < parseFloat(leaseFrom.value)
      }))
    )

    const downpaymentFromOptions = computed(() =>
      props.leasingOptionsFacet.searchables.downpayment.map(
        (s: Searchable) => ({
          label: s.n,
          value: s.v,
          disabled:
            downpaymentTo.value !== null &&
            parseFloat(s.v) > parseFloat(downpaymentTo.value)
        })
      )
    )

    const downpaymentToOptions = computed(() =>
      props.leasingOptionsFacet.searchables.downpayment.map(
        (s: Searchable) => ({
          label: s.n,
          value: s.v,
          disabled:
            downpaymentFrom.value !== null &&
            parseFloat(s.v) < parseFloat(downpaymentFrom.value)
        })
      )
    )

    const durationOptions = computed(() =>
      props.leasingOptionsFacet.searchables.duration.map((s: Searchable) => ({
        label: s.n,
        value: s.v
      }))
    )

    const mileageOptions = computed(() =>
      props.leasingOptionsFacet.searchables.mileage.map((s: Searchable) => ({
        label: s.n,
        value: s.v
      }))
    )

    const extrasOptions = computed(() =>
      props.leasingExtrasFacet.searchables.map((s: Searchable) => ({
        label: s.n,
        value: s.v
      }))
    )

    watch(
      () => props.leasingOptionsFacet,
      (newLeasingOptionsFacet: Facet) => {
        leaseFrom.value = newLeasingOptionsFacet?.values.selected['lease-from']
        leaseTo.value = newLeasingOptionsFacet?.values.selected['lease-to']
        downpaymentFrom.value =
          newLeasingOptionsFacet?.values.selected['lease-downpayment-from']
        downpaymentTo.value =
          newLeasingOptionsFacet?.values.selected['lease-downpayment-to']
        duration.value = newLeasingOptionsFacet?.values.selected.duration
        withVat.value = newLeasingOptionsFacet?.values.selected['with-vat']
      }
    )

    watch(
      () => props.leasingExtrasFacet,
      (newLeasingExtrasFacet: Facet) => {
        extras.value = newLeasingExtrasFacet.values.selected
      }
    )

    watch(totalStatsResults, (newTotal: number) => {
      resultsCount.value = newTotal
    })

    async function loadClassifiedsCount() {
      loadingCount.value = true

      const data = await searchService.getFacets({
        ...classifiedSearchState.config.rootParams,
        ...classifiedSearchState.params,
        ...{
          'lease-from': leaseFrom.value,
          'lease-to': leaseTo.value,
          'lease-downpayment-from': downpaymentFrom.value,
          'lease-downpayment-to': downpaymentTo.value,
          'with-vat': withVat.value,
          duration: duration.value,
          'yearly-mileage': mileage.value,
          'leasing-extra': extras.value
        }
      })

      resultsCount.value = data.stats.total
      loadingCount.value = false
    }

    function onShowButtonClick() {
      hasSomeFieldChanged.value = false
      emitValues()
    }

    function onFieldChange() {
      hasSomeFieldChanged.value = true

      // The data is only emitted when the user clicks the "Show" button on desktop
      if (isPc.value) {
        loadClassifiedsCount()
      } else {
        emitValues()
      }
    }

    function emitValues() {
      emit('change', {
        leaseFrom: leaseFrom.value,
        leaseTo: leaseTo.value,
        downpaymentFrom: downpaymentFrom.value,
        downpaymentTo: downpaymentTo.value,
        withVat: withVat.value,
        duration: duration.value,
        mileage: mileage.value,
        extras: extras.value
      })
    }

    return {
      isPc,
      loadingCount,
      loadingFacets,
      resultsCount,
      extras,
      extrasOptions,
      leaseFromOptions,
      leaseToOptions,
      downpaymentFromOptions,
      downpaymentToOptions,
      durationOptions,
      mileageOptions,
      duration,
      leaseFrom,
      leaseTo,
      downpaymentFrom,
      downpaymentTo,
      withVat,
      mileage,
      hasSomeFieldChanged,
      onShowButtonClick,
      formatNumber,
      onFieldChange
    }
  },
  i18n: defineComponentTranslations({
    lease_per_month: {
      en: 'Lease / month',
      el: 'Μίσθωση / μήνα'
    },
    mileage_per_year: {
      en: 'Mileage / year',
      el: 'Χιλιόμετρα / έτος'
    },
    select_extras: {
      en: 'Select extras',
      el: 'Επίλεξε παροχές'
    },
    free_amenities: {
      en: 'Free amenities',
      el: 'Δωρεάν παροχές'
    },
    select_duration: {
      en: 'Select duration',
      el: 'Επίλεξε διάρκεια'
    },
    select_mileage: {
      en: 'Select mileage',
      el: 'Επίλεξε χιλιόμετρα'
    }
  })
})
